import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Notification,
  Message,
} from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DisplayDate, DisplayGuid } from "../items";

const AutomatedBookingAgreementInternal = (props) => {
  const {
    match: {
      params: { agreementId },
    },
  } = props;

  const [agreement, setAgreement] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    async function loadAgreement() {
      try {
        const data = await props.api.automatedBookingAgreements.getAgreementById(
          agreementId
        );
        setAgreement(data);
      } catch (error) {
        setNotification(new Message("error", "Unable to load agreement."));
      }
    }

    loadAgreement();
  }, [agreementId]);

  const renderAttemptTable = () => {
    if (!agreement?.attempts || agreement.attempts.length === 0) {
      return null;
    }

    const attemptHeaders = [
      { key: "id", label: "Attempt Id" },
      { key: "result", label: "Result" },
      { key: "slotStartCet", label: "Slot Start (CET)" },
      { key: "slotEndCet", label: "Slot End (CET)" },
      { key: "chosenCourtName", label: "Chosen Court" },
      { key: "activityId", label: "Activity" },
      { key: "signupId", label: "Signup" },
      { key: "createdAtCet", label: "Created (CET)" },
      { key: "bookableFromCet", label: "Bookable From (CET)" },
      { key: "message", label: "Message" },
    ];

    return (
      <article className="data-table-wrap">
        <h3 className="main-page-header main-page-header-with-subheader">
          Attempts
        </h3>
        <br />
        <table className="data-lists component-data-table cell-content-align-top">
          <thead>
            <tr>
              {attemptHeaders.map((column) => (
                <td key={column.key}>{column.label}</td>
              ))}
            </tr>
          </thead>
          {agreement.attempts.map((attempt) => (
            <tbody key={attempt.id}>
              <tr className="hover-listview ">
                {attemptHeaders.map((column) => {
                  const value = attempt[column.key];
                  let content = value;

                  switch (column.key) {
                    case "id":
                      content = <DisplayGuid>{attempt.id}</DisplayGuid>;
                      break;
                    case "slotStartCet":
                      content = <DisplayDate breakTime>{attempt.slotStartCet}</DisplayDate>;
                      break;
                    case "slotEndCet":
                      content = <DisplayDate breakTime>{attempt.slotEndCet}</DisplayDate>;
                      break;
                    case "createdAtCet":
                      content = <DisplayDate breakTime>{attempt.createdAtCet}</DisplayDate>;
                      break;
                    case "bookableFromCet":
                      content = <DisplayDate breakTime>{attempt.bookableFromCet}</DisplayDate>;
                      break;
                    case "activityId":
                      content = attempt.activityId ? (
                        <DisplayGuid baseAnchor={"/activity"}>
                          {attempt.activityId}
                        </DisplayGuid>
                      ) : (
                        "-"
                      );
                      break;
                    case "signupId":
                      content = attempt.signupId ? (
                        <DisplayGuid baseAnchor={"/signup"}>
                          {attempt.signupId}
                        </DisplayGuid>
                      ) : (
                        "-"
                      );
                      break;
                    case "message":
                      content = value ? <span>{value}</span> : "-";
                      break;
                    case "result":
                      content = value || "-";
                      break;
                    case "chosenCourtName":
                      content = value || "-";
                      break;
                    default:
                      break;
                  }

                  return (
                    <td key={column.key}>
                      <strong className="table-cell-label">{column.label}</strong>
                      {content}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          ))}
        </table>
      </article>
    );
  };

  const renderContent = () => {
    if (!agreement) {
      return <span>Loading...</span>;
    }

    const generalData = {
      "Agreement Id": <DisplayGuid>{agreement.id}</DisplayGuid>,
      Status: agreement.status,
      User: (
        <span>
          <DisplayGuid baseAnchor={"/user"}>{agreement.userId}</DisplayGuid>
          {" "}
          {agreement.userName ? `${agreement.userName}` : ""}
          {agreement.userEmail ? ` - ${agreement.userEmail}` : ""}
        </span>
      ),
      Product: (
        <span>
          <DisplayGuid baseAnchor={"/product"}>{agreement.productId}</DisplayGuid>
          {" "}
          {agreement.productName || ""}
        </span>
      ),
      Facility: (
        <span>
          <DisplayGuid baseAnchor={"/facility"}>{agreement.facilityId}</DisplayGuid>
          {" "}
          {agreement.facilityName || ""}
        </span>
      ),
      "Day / Time": `${agreement.dayOfWeek} ${agreement.timeOfDay}`,
      Duration: `${agreement.durationMinutes} min`,
      "Preferred Court": agreement.preferredCourtName || "-",
      "Start Date (CET)": agreement.startDateCet ? (
        <DisplayDate breakTime>{agreement.startDateCet}</DisplayDate>
      ) : (
        "-"
      ),
      "End Date (CET)": agreement.endDateCet ? (
        <DisplayDate breakTime>{agreement.endDateCet}</DisplayDate>
      ) : (
        "-"
      ),
      "Created (CET)": (
        <DisplayDate breakTime>{agreement.createdAtCet}</DisplayDate>
      ),
      "Updated (CET)": agreement.updatedAtCet ? (
        <DisplayDate breakTime>{agreement.updatedAtCet}</DisplayDate>
      ) : (
        "-"
      ),
    };

    const statsData = {
      "Attempts": agreement.attemptCount,
      "Successful Attempts": agreement.successfulAttemptCount,
      "Failed Attempts": agreement.failedAttemptCount,
      "Last Attempt": agreement.lastAttemptResult ? (
        <>
          {agreement.lastAttemptResult}
          {agreement.lastAttemptStartedAtCet && (
            <>
              <br />
              <DisplayDate breakTime>
                {agreement.lastAttemptStartedAtCet}
              </DisplayDate>
            </>
          )}
        </>
      ) : (
        "-"
      ),
      "Last Successful": agreement.lastSuccessfulAttemptCet ? (
        <DisplayDate breakTime>{agreement.lastSuccessfulAttemptCet}</DisplayDate>
      ) : (
        "-"
      ),
      "Upcoming Scheduled": agreement.upcomingScheduledAttemptCet ? (
        <DisplayDate breakTime>{agreement.upcomingScheduledAttemptCet}</DisplayDate>
      ) : (
        "-"
      ),
    };

    return (
      <>
        <DataDefinitionList listData={generalData} classNames={["detail-page-list"]} />
        <DataDefinitionList listData={statsData} classNames={["detail-page-list"]} />
        {renderAttemptTable()}
      </>
    );
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop
              documentTitle="Automated Booking Agreement"
              documentDescription="Automated Booking Agreement"
            />
            <h2 className="main-page-header">Agreement details</h2>
            <h5 className="main-page-subheader">{agreementId}</h5>
            <br />
            {renderContent()}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const AutomatedBookingAgreement = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(
      AutomatedBookingAgreementInternal
    )
  )
);

AutomatedBookingAgreement.requiresLogin = true;
AutomatedBookingAgreement.propTypes = { isLoggedIn: PropTypes.bool };

export default AutomatedBookingAgreement;
